import { useCallback, useMemo } from "react";

/**
 * Custom hook for showing toast notifications
 *
 * @example
 * const toast = useToast();
 * toast.success('Operation completed!');
 * toast.error('Something went wrong', 'Error Title');
 * toast.info('New message received');
 * toast.warn('Please check your input');
 */
const useToast = () => {
  const showToast = useCallback((variant, message, title, options = {}) => {
    const event = new CustomEvent("show-toast", {
      detail: {
        variant,
        message,
        title,
        duration: options.duration,
        position: options.position,
        icon: options.icon,
        showCloseButton: options.showCloseButton,
      },
    });

    window.dispatchEvent(event);
  }, []);

  return useMemo(
    () => ({
      success: (message, title, options) =>
        showToast("success", message, title, options),
      error: (message, title, options) =>
        showToast("error", message, title, options),
      warn: (message, title, options) =>
        showToast("warn", message, title, options),
      info: (message, title, options) =>
        showToast("info", message, title, options),
      show: (variant, message, title, options) =>
        showToast(variant, message, title, options),
    }),
    [showToast]
  );
};

export default useToast;
